import {
  ActiveCompany,
  CompanyAddress,
  ILoadedVenue,
  IUserCompanyInfoDTO,
} from '$models'
import logger from './logger'

// TODO - I'd like to rewrite this as a Zustand store that can be pulled into
// any component that needs the data managed here. Potentially with nested
// Zustand stores being put in place we can remove the need to reset the state
// in components when remounting to ensure we pick up the latest data from this
// store. See the ./store/appState.ts file for an example of how this could be
// done (they may end up being merged).

// Stores the current state of the app context. This is populated once we hear
// from the react bridge (to the AngularJS app) that it is ready to be read.
const CurrentAppContext: AppContextType = {
  ready: false,
  data: undefined,
}

// Starts listening for notifications from AngularJS that data is able to be
// read. Note that we continue listening as if the company changes in the
// AngularJS app we want to ensure the updated company data is being used in
// the React app too.
window.addEventListener('@@loaded/appContextSet', () => {
  logger.debug('Received notification the app context has been set')

  window.__Loaded.reactBridge.appContext().then((data) => {
    logger.debug('Received app context data %o', data)
    CurrentAppContext.ready = true
    CurrentAppContext.data = data
  })
})

// Also listen for notification of context changes and update the data too.
window.addEventListener('@@loaded/appContextChange', (ev: Event) => {
  const { type } = (ev as CustomEvent).detail
  logger.debug(
    'Received notification the app context %s data has changed',
    type
  )

  window.__Loaded.reactBridge.appContext().then((data) => {
    logger.debug('Received updated app context data %o', data)
    CurrentAppContext.data = data
  })
})

export function isReady() {
  const readyPromise = new Promise<void>((resolve) => {
    const interval = setInterval(() => {
      if (CurrentAppContext.ready) {
        logger.info('AppContext is now ready.')
        clearInterval(interval)
        resolve()
      }
    }, 15)
  })

  return readyPromise
}

/**
 * Returns the active company data and a function to read a preference for that
 * company.
 *
 * @returns
 */
export function getActiveCompany() {
  return {
    id: CurrentAppContext.data?.activeCompany.id,
    name: CurrentAppContext.data?.activeCompany.name,
    pos: CurrentAppContext.data?.activeCompany.pos,
    invoiceEmail: CurrentAppContext.data?.activeCompany.invoiceEmail,
    country: CurrentAppContext.data?.activeCompany.country,
    tier: CurrentAppContext.data?.activeCompany.subscriptionTier,
    address: CurrentAppContext.data?.activeCompany.address,
    hasPermission: checkPermission,
    preference: readPreference,
  } as ActiveCompany
}

/**
 * Returns the venues that are accesible by the Loaded User
 */
export function getAllVenues(): ILoadedVenue[] {
  if (!CurrentAppContext.data?.allCompanies) {
    return []
  }

  return CurrentAppContext.data.allCompanies.map((venue) => {
    const permissionsArray = venue.permissions
      ? venue.permissions.split(',')
      : []

    return {
      shortName: venue.name,
      ...venue,
      permissions: permissionsArray,
      hasPermission: (permissionName: PermissionType) =>
        checkVenuePermission(permissionsArray, permissionName),
    }
  })
}

export function getUserDetails() {
  return {
    ...CurrentAppContext.data?.userDetails,
  }
}

/* internal functions and types below here */

/**
 * Checks if a venue has a specific permission from its permissions array.
 * The check is case-insensitive and trims whitespace from permission names.
 *
 * @param permissionsArray - Array of permission strings for the venue
 * @param permissionName - The permission type to check for
 * @returns true if the venue has the specified permission, false otherwise
 */
function checkVenuePermission(
  permissionsArray: string[],
  permissionName: PermissionType
) {
  if (!permissionsArray || permissionsArray.length === 0) {
    return false
  }

  return permissionsArray.some(
    (permission) =>
      permission.toLowerCase().trim() === permissionName.toLowerCase()
  )
}

function checkPermission(permissionName: PermissionType) {
  if (!CurrentAppContext.ready) {
    logger.warn('AppContext is not ready')
    throw new Error(
      'Attempted to read a company permission before the AppContext is ready.'
    )
  }

  if (!CurrentAppContext.data) {
    logger.warn('AppContext is missing activeCompany data')
    throw new Error(
      'Attempted to read a company permission with no active company available.'
    )
  }

  const { activeCompany } = CurrentAppContext.data
  if (!activeCompany) {
    logger.warn('No active company in AppContext')
    return false
  }

  const { permissions } = activeCompany
  if (!permissions) {
    logger.warn('No permissions in active company')
    return false
  }

  return permissions.toLowerCase().includes(permissionName.toLowerCase())
}

function readPreference(preferenceName: CompanyPreferences) {
  if (!CurrentAppContext.ready) {
    logger.warn('AppContext is not ready')
    throw new Error(
      'Attempted to read a company preference before the AppContext is ready.'
    )
  }

  if (!CurrentAppContext.data) {
    logger.warn('AppContext is missing activeCompany data')
    throw new Error(
      'Attempted to read a company preference wit no active company available.'
    )
  }

  const { activeCompany } = CurrentAppContext.data
  if (!activeCompany) {
    logger.warn('No active company in AppContext')
    return null
  }

  const { preferences } = activeCompany
  if (!preferences) {
    logger.warn('No preferences in active company')
    return null
  }

  const preference = preferences[preferenceName]
  if (!preference) {
    logger.warn('No preference with name %s in active company', preferenceName)
    return null
  }

  return preference
}

// This code relies on reading a global variable that is defined in the AngularJS
// app. We specify it's shape here so that TS knows what to expect.
type LoadedGlobal = {
  reactBridge: {
    appContext: () => Promise<AppContextData>
  }
}
export interface LoadedWindow extends Window {
  __Loaded: LoadedGlobal
}
declare let window: LoadedWindow

/**
 * The company preferences that are avaiable.
 */
export enum CompanyPreferences {
  DayStart = 'date.daycutoff.daystart',
  WeekStart = 'date.daycutoff.weekstart',
  Locale = 'locale.locale',
  Province = 'locale.province',
  TimeZone = 'locale.timeZone',
  TimePermissionAllowsWageAndSalarySummary = 'permissions.timeAllowsSummary',
  DefaultFreightTaxRate = 'tax.defaultFreightRate',
  OverrideSalesTaxRate = 'tax.overrideSalesTax',
  ClockinRounding = 'time.clockinRounding',
  IncludePosClockins = 'time.includePosClockins',
  PayrollSystem = 'time.payrollSystem',
  TimeRosterPublicToken = 'time.rosterPublicToken',
  XeroFreightAccount = 'xero.freightaccount',
  TeamAppEnabled = 'teamApp.enabled',
  TeamAppDisplayPayInformation = 'teamApp.displayPayInformation',
  TeamAppDisplayPhoneNumbers = 'teamApp.displayPhoneNumbers',
}

export enum PermissionType {
  Manager = 'Manager',
  Report = 'Report',
  Cashup = 'Cashup',
  Stock = 'Stock',
  StockShare = 'StockShare',
  Payroll = 'Payroll',
  Salary = 'Salary',
  Time = 'Time',
}

type AppContextType = {
  ready: boolean
  data?: AppContextData
}

type AppContextData = {
  activeCompany: ActiveCompanyType
  userDetails: UserDetails
  allCompanies: IUserCompanyInfoDTO[]
}

type ActiveCompanyType = {
  id: string
  name: string
  preferences: CompanyPreferences
  permissions: PermissionType
  pos: string
  invoiceEmail: string
  country: string
  subscriptionDenied: boolean
  subscriptionState: number
  subscriptionTier: string
  address: CompanyAddress
}

type UserDetails = {
  email: string
  firstName: string
  lastName: string
  hasPassword: boolean
  id: string
  isLegacyUser: boolean
  name: string
}
